import { useTranslation } from 'react-i18next'
import { Heading, InfoDialog } from '@repo/ui'
import { ChatIcon32, DoneIcon32, FolderOpenIcon32 } from '@repo/ui/icons'
import BfnAbrufen from '@/features/befehlsnachrichten/components/BfnAbrufen'
import EmptyState from '@/components/common/EmptyState'
import BfnListItem from '@/features/befehlsnachrichten/components/BfnListItem'
import Toggle from '@/components/common/form/Toggle'
import { FormProvider } from 'react-hook-form'
import { useEffect } from 'react'
import { scrollIntoView } from '@repo/ui/utils/Scroll'
import { useLocation } from 'react-router-dom'
import useBfnOverviewFilters from '@/features/befehlsnachrichten/hooks/useBfnOverviewFilters'
import useBfnZugnummerLogin from '../hooks/useBfnZugnummerLogin'
import ZugnummerDisplay from '@/components/common/ZugnummerDisplay'
import useBfnOverviewEntries from '../hooks/useBfnOverviewEntries'

export const ID_RELEVANT_BFNS = 'relevant-bfns'

export default function BfnOverview() {
  const { t } = useTranslation('bfnOverview')

  const { form, showArchiv } = useBfnOverviewFilters()
  const { register } = form
  const { showArchivHinweis, dismissArchivHinweis, effectiveZugnummer } =
    useBfnZugnummerLogin()

  const location = useLocation()

  const testIdInit = 'bfn-overview'

  useEffect(() => {
    if (location.state?.scrollTo) {
      scrollIntoView(location.state.scrollTo)
    }
  }, [location.state])

  const { relevantEntries, archivedEntries } =
    useBfnOverviewEntries(effectiveZugnummer)

  const shouldShowAbrufHint =
    effectiveZugnummer !== '' && relevantEntries.length == 0

  return (
    <>
      <Heading tag="h1">{t('heading')}</Heading>
      <BfnAbrufen data-testid={`${testIdInit}-abrufen`} />
      <div id={ID_RELEVANT_BFNS} className="flex flex-col gap-1 mt-2.5">
        <ZugnummerDisplay
          className="shrink-0 whitespace-nowrap"
          data-testid={testIdInit}
        />

        {relevantEntries.length > 0 ? (
          <div className="flex flex-col gap-1 lg:grid lg:grid-cols-2">
            {relevantEntries.map(([id, entry], index) => (
              <BfnListItem
                key={id}
                id={id}
                data={entry}
                data-testid={`${testIdInit}-item-${index}`}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={ChatIcon32}
            title={
              shouldShowAbrufHint
                ? t('zugnummer_locked_title')
                : t('empty_state_title')
            }
            text={
              shouldShowAbrufHint
                ? t('zugnummer_locked_text')
                : t('empty_state_text')
            }
            data-testid={`${testIdInit}-empty-state`}
          />
        )}
      </div>
      <div className="flex flex-col gap-1 mt-2.5">
        <div className="flex flex-row items-center min-h-2">
          {showArchiv && (
            <div className="flex gap-0.25">
              <FolderOpenIcon32 data-testid={`${testIdInit}-archive-icon`} />
              <span className="text-xl font-bold shrink-0 flex-nowrap">
                {t('archive.label')}
              </span>
            </div>
          )}
          <FormProvider {...form}>
            <form className={'ml-auto'}>
              <Toggle
                label={t('archive.label')}
                className="shrink-0"
                data-testid={`${testIdInit}-toggle`}
                {...register('archiv')}
              />
            </form>
          </FormProvider>
        </div>

        {showArchiv &&
          (archivedEntries.length > 0 ? (
            <div className="flex flex-col gap-1 lg:grid lg:grid-cols-2 ">
              {archivedEntries.map(([id, entry], index) => (
                <BfnListItem
                  key={id}
                  id={id}
                  data={entry}
                  data-testid={`${testIdInit}-archive-item-${index}`}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={ChatIcon32}
              title={t('archive.empty_state_archive_title')}
              text={t('archive.empty_state_archive_text')}
              data-testid={`${testIdInit}-archive-empty-state`}
            />
          ))}
      </div>

      {showArchivHinweis && (
        <InfoDialog
          title={t('archive.hint.title')}
          state={true}
          onOk={dismissArchivHinweis}
          onClose={dismissArchivHinweis}
          okButtonText={t('archive.hint.confirm')}
          okButtonIcon={DoneIcon32}
          data-testid={`${testIdInit}-archiv-hinweis-dialog`}
        >
          <p>{t('archive.hint.text')}</p>
        </InfoDialog>
      )}
    </>
  )
}
